import { Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PlaylistItem {
  snippet: {
    title: string;
    thumbnails: {
      standard?: { url: string };
      high?: { url: string };
      default: { url: string };
    };
    resourceId: {
      videoId: string;
    };
  };
}

interface YouTubeResponse {
  items: PlaylistItem[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function YouTubeVideos() {
  const [videos, setVideos] = useState<PlaylistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isMuted, setIsMuted] = useState(true);
  const [showMuteButton, setShowMuteButton] = useState(true);
  
  const AWSLambdaEndpoint = "https://37k7ff52vk3ssdtpn6geg4hvhu0tnycm.lambda-url.eu-west-2.on.aws/"

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(AWSLambdaEndpoint);
        if (!response.ok) {
          throw new Error('Failed to fetch videos');
        }
        const data: YouTubeResponse = await response.json();
        setVideos(data.items);
        if (data.items.length > 0) {
          setActiveVideo(data.items[0].snippet.resourceId.videoId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleNext = () => {
    if (activeIndex < videos.length - 1) {
      const newIndex = activeIndex + 1;
      setActiveIndex(newIndex);
      setActiveVideo(videos[newIndex].snippet.resourceId.videoId);
      setShowMuteButton(true);
      delayShowMuteButton()
    }
  };

  const delayShowMuteButton = () => {
    setTimeout(() => {
      if (!isMuted) {
      setShowMuteButton(false);
      }
    }, 1500);
  }

  const handlePrevious = () => {
    if (activeIndex > 0) {
      const newIndex = activeIndex - 1;
      setActiveIndex(newIndex);
      setActiveVideo(videos[newIndex].snippet.resourceId.videoId);
      setShowMuteButton(true);
      delayShowMuteButton()
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    setShowMuteButton(true);
    // Hide the button after a short delay
    delayShowMuteButton()
  };

  if (loading) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        variants={item}
        className="text-lg font-semibold mb-3"
      >
        Latest Videos
      </motion.h2>
      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-2"
      >
        <motion.div
          variants={item}
          className="relative aspect-video w-full rounded-lg bg-gray-800 animate-pulse"
        />
      </motion.div>
    </motion.div>
  );
  
  if (error) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        variants={item}
        className="text-lg font-semibold mb-3"
      >
        Latest Videos
      </motion.h2>
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-red-500"
      >
        Error: {error}
      </motion.div>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        variants={item}
        className="text-lg font-semibold mb-3"
      >
        Latest Videos
      </motion.h2>
      <AnimatePresence mode="wait">
        {activeVideo && (
          <motion.div 
            key={activeVideo}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black min-h-[200px]">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&mute=${isMuted ? 1 : 0}&modestbranding=1&controls=1&rel=0&showinfo=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
              
              <AnimatePresence>
                {showMuteButton && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="hidden md:block"
                  >
                    <div className="absolute inset-0 bg-transparent z-10" />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={toggleMute}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 bg-black/30 hover:bg-black/50 rounded-full transition-all duration-300 flex items-center justify-center z-40"
                    >
                      {isMuted ? (
                        <VolumeX className="w-12 h-12 pointer-events-none" />
                      ) : (
                        <Volume2 className="w-12 h-12 pointer-events-none" />
                      )}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="absolute top-1/2 left-0 -translate-y-1/2 flex items-center justify-between w-full px-4 z-30">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePrevious}
                  disabled={activeIndex === 0}
                  className={`p-2 bg-black/30 hover:bg-black/50 rounded-full transition-colors ${
                    activeIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronLeft className="w-6 h-6" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleNext}
                  disabled={activeIndex === videos.length - 1}
                  className={`p-2 bg-black/30 hover:bg-black/50 rounded-full transition-colors ${
                    activeIndex === videos.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronRight className="w-6 h-6" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
